import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Flame,
  ChevronDown,
  Lock,
  MessageCircle,
  Loader2,
  Send,
  CheckCircle2,
  UserPlus,
  ExternalLink,
  Paperclip,
  Wrench,
  ArrowRight,
  Clock,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { cn, formatDateTime } from "@/lib/utils";
import { useAuthStore } from "@/lib/auth";
import { useQueryClient } from "@tanstack/react-query";
import {
  invalidateAll as invalidateAllTickets,
  useAddComentario,
  useAssignTicket,
  useChangeTicketState,
  uploadAdjuntoTicket,
  useTicket,
  useUpdateTicket,
} from "./api";
import { AttachmentGrid } from "./AttachmentThumb";
import {
  TICKET_CATEGORIA_LABEL,
  TICKET_ESTADO_LABEL,
  TICKET_PRIORIDAD_LABEL,
  ticketCategoriaEnum,
  ticketEstadoEnum,
  ticketPrioridadEnum,
  type ComentarioOut,
  type TicketCategoria,
  type TicketDetail,
  type TicketEstado,
  type TicketPrioridad,
} from "./schemas";
import { AsignarTicketDialog } from "./AsignarTicketDialog";

// ── Estilos compartidos pa' pills ───────────────────────────────────────
const PILL_PRIO: Record<TicketPrioridad, string> = {
  baja: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  normal: "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  alta: "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  urgente: "bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
};
const PILL_ESTADO: Record<string, string> = {
  abierto: "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  en_proceso: "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  resuelto: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  cerrado: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
};
const PILL_CAT: Record<TicketCategoria, string> = {
  soporte_tecnico: "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  facturacion: "bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  instalacion: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  cancelacion: "bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
  otro: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
};

function avatarFromName(name?: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ─────────────────────────────────────────────────────────────────────────
export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: ticket, isLoading, error } = useTicket(id ?? null);

  if (isLoading || !ticket) {
    return (
      <div className="flex flex-col h-full">
        <header className="h-12 flex items-center px-4 gap-2 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
          <button onClick={() => navigate("/tickets")} className="text-zinc-500 hover:text-zinc-900 flex items-center gap-1 text-xs">
            <ArrowLeft size={16} />
            Tickets
          </button>
        </header>
        <div className="flex-1 flex items-center justify-center text-sm text-zinc-500">
          {error ? "Ticket no encontrado" : "Cargando…"}
        </div>
      </div>
    );
  }

  return <TicketDetailContent ticket={ticket} />;
}

// ─────────────────────────────────────────────────────────────────────────
function TicketDetailContent({ ticket }: { ticket: TicketDetail }) {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  const update = useUpdateTicket(ticket.id);
  const assign = useAssignTicket(ticket.id);
  const stateChg = useChangeTicketState(ticket.id);
  const addCom = useAddComentario(ticket.id);

  const [reply, setReply] = useState("");
  const [isInternal, setIsInternal] = useState(false);

  // Adjunto opcional al comentar (v1.4.0)
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // rc45 — dialog pa asignar a otro user (no solo self-assign)
  const [asignarOpen, setAsignarOpen] = useState(false);

  const isCerrado = ticket.estado === "cerrado";

  async function setEstado(estado: TicketEstado) {
    try {
      await stateChg.mutateAsync({ estado });
      toast.success(`Estado: ${TICKET_ESTADO_LABEL[estado]}`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Error al cambiar estado");
    }
  }
  async function setPrioridad(prioridad: TicketPrioridad) {
    try {
      await update.mutateAsync({ prioridad });
      toast.success(`Prioridad: ${TICKET_PRIORIDAD_LABEL[prioridad]}`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Error al cambiar prioridad");
    }
  }
  async function setCategoria(categoria: TicketCategoria) {
    try {
      await update.mutateAsync({ categoria });
      toast.success(`Categoría: ${TICKET_CATEGORIA_LABEL[categoria]}`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Error al cambiar categoría");
    }
  }
  async function takeIt() {
    if (!currentUser) return;
    try {
      await assign.mutateAsync(currentUser.id);
      toast.success("Ticket asignado a ti");
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Error al asignar");
    }
  }
  async function unassign() {
    try {
      await assign.mutateAsync(null);
      toast.success("Asignación removida");
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Error");
    }
  }

  async function submitReply(opts: { resolveAfter?: boolean } = {}) {
    if (!reply.trim()) return;
    try {
      const created = await addCom.mutateAsync({
        cuerpo: reply.trim(),
        interno: isInternal,
      });

      // Si hay foto adjunta, subir y ligar al comentario recién creado.
      // Importante: invalidate DESPUÉS del upload — el mutateAsync del
      // comentario ya invalidó pero el adjunto se sube después, así que
      // forzamos otro refetch pa' que el thread muestre la imagen sin F5.
      if (attachment) {
        setIsUploading(true);
        try {
          await uploadAdjuntoTicket(ticket.id, attachment, created.id);
          invalidateAllTickets(qc);
        } catch (err) {
          toast.error(
            `Comentario enviado, pero falló adjuntar: ${
              (err as Error).message ?? "Error"
            }`,
          );
        } finally {
          setIsUploading(false);
        }
      }

      setReply("");
      setIsInternal(false);
      setAttachment(null);
      toast.success(isInternal ? "Nota interna agregada" : "Respuesta enviada");
      if (opts.resolveAfter && !isInternal) {
        await stateChg.mutateAsync({ estado: "resuelto" });
        toast.success("Ticket resuelto");
      }
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Error al enviar");
    }
  }

  return (
    <div className="flex flex-col h-full bg-zinc-50 dark:bg-zinc-950">
      {/* Topbar */}
      <header className="h-12 flex items-center px-4 gap-2 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 flex-shrink-0">
        <button
          onClick={() => navigate("/tickets")}
          className="text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 flex items-center gap-1 text-xs"
        >
          <ArrowLeft size={16} />
          Tickets
        </button>
        <span className="text-zinc-300 dark:text-zinc-700">/</span>
        <span className="text-xs font-mono text-zinc-500">{ticket.codigo}</span>
        <div className="ml-auto flex items-center gap-1 text-xs text-zinc-500 max-md:hidden">
          <Clock size={14} />
          Última actividad {formatDateTime(ticket.ultimo_mensaje_at)}
        </div>
      </header>

      {/* Layout: thread + sidebar derecho.
          rc137 — móvil: una sola columna que scrollea completa; el panel derecho
          (cliente, acciones, auditoría) se apila debajo del hilo. */}
      <div className="flex-1 overflow-hidden flex max-md:flex-col max-md:overflow-y-auto">
        {/* Thread */}
        <div className="flex-1 overflow-y-auto max-md:flex-none max-md:overflow-visible">
          {/* Header del ticket */}
          <div className="bg-white dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800 px-6 py-5 max-md:px-4 max-md:py-3">
            <div className="flex items-start gap-3 mb-3">
              <span className="font-mono text-xs text-zinc-500 bg-zinc-100 dark:bg-zinc-900 px-2 py-1 rounded mt-1 flex-shrink-0">
                {ticket.codigo}
              </span>
              <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 leading-snug flex-1 max-md:text-base">
                {ticket.asunto}
              </h1>
            </div>

            {/* Meta editable */}
            <div className="flex items-center gap-3 flex-wrap text-xs max-md:gap-x-2 max-md:gap-y-2">
              <MetaCell label="Cliente">
                <button
                  type="button"
                  onClick={() => navigate(`/clientes/${ticket.cliente.id}`)}
                  className="text-zinc-900 dark:text-zinc-100 font-medium hover:underline"
                >
                  {ticket.cliente.razon_social}
                </button>
                <span className="text-zinc-400 font-mono text-[11px]">
                  ({ticket.cliente.codigo})
                </span>
              </MetaCell>
              <Sep />
              <MetaCell label="Estado">
                <PillSelect
                  value={ticket.estado}
                  options={ticketEstadoEnum.map((v) => ({ value: v, label: TICKET_ESTADO_LABEL[v] }))}
                  className={PILL_ESTADO[ticket.estado]}
                  onChange={(v) => setEstado(v as TicketEstado)}
                  disabled={stateChg.isPending}
                />
              </MetaCell>
              <Sep />
              <MetaCell label="Prioridad">
                <PillSelect
                  value={ticket.prioridad}
                  options={ticketPrioridadEnum.map((v) => ({
                    value: v,
                    label: TICKET_PRIORIDAD_LABEL[v],
                  }))}
                  className={PILL_PRIO[ticket.prioridad]}
                  onChange={(v) => setPrioridad(v as TicketPrioridad)}
                  disabled={update.isPending}
                  prefix={ticket.prioridad === "urgente" ? <Flame size={10} /> : undefined}
                />
              </MetaCell>
              <Sep />
              <MetaCell label="Categoría">
                <PillSelect
                  value={ticket.categoria}
                  options={ticketCategoriaEnum.map((v) => ({
                    value: v,
                    label: TICKET_CATEGORIA_LABEL[v],
                  }))}
                  className={PILL_CAT[ticket.categoria]}
                  onChange={(v) => setCategoria(v as TicketCategoria)}
                  disabled={update.isPending}
                />
              </MetaCell>
              <Sep />
              <MetaCell label="Asignado">
                {ticket.asignado ? (
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setAsignarOpen(true)}
                      title="Cambiar asignación"
                      className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                    >
                      <div className="w-4 h-4 rounded-full bg-violet-500 text-white flex items-center justify-center text-[8px] font-bold">
                        {avatarFromName(ticket.asignado.nombre)}
                      </div>
                      <span className="text-xs text-zinc-700 dark:text-zinc-300">
                        {ticket.asignado.nombre}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={unassign}
                      className="text-zinc-400 hover:text-rose-600"
                      title="Desasignar"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={takeIt}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900 text-xs"
                      title="Asignármelo a mí"
                    >
                      <UserPlus size={11} />
                      Tomarlo
                    </button>
                    <button
                      type="button"
                      onClick={() => setAsignarOpen(true)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 dark:bg-violet-950 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900 text-xs"
                      title="Asignar a otro usuario"
                    >
                      Asignar a…
                    </button>
                  </div>
                )}
              </MetaCell>
            </div>
          </div>

          {/* Comentario inicial = la descripción del ticket */}
          <CommentRow
            authorName={
              ticket.creado_por_cliente
                ? ticket.cliente.razon_social
                : ticket.creador?.nombre ?? "Sistema"
            }
            authorTag={ticket.creado_por_cliente ? "cliente" : "tecnico"}
            createdAt={ticket.creado_at}
            body={ticket.descripcion}
            internal={false}
            attachments={
              ticket.adjuntos_iniciales.length > 0 ? (
                <AttachmentGrid
                  ticketId={ticket.id}
                  adjuntos={ticket.adjuntos_iniciales}
                />
              ) : null
            }
          />

          {/* Hilo de comentarios */}
          {ticket.comentarios.map((c) => (
            <CommentRow
              key={c.id}
              authorName={
                c.autor_es_cliente
                  ? ticket.cliente.razon_social
                  : c.autor?.nombre ?? "Sistema"
              }
              authorTag={c.autor_es_cliente ? "cliente" : "tecnico"}
              createdAt={c.creado_at}
              body={c.cuerpo}
              internal={c.interno}
              attachments={
                c.adjuntos.length > 0 ? (
                  <AttachmentGrid ticketId={ticket.id} adjuntos={c.adjuntos} />
                ) : null
              }
            />
          ))}

          {/* Eventos derivados (resuelto / cerrado) */}
          {ticket.resuelto_at && (
            <EventRow icon={<CheckCircle2 size={12} />}>
              {ticket.resuelto_por ? (
                <>
                  <strong>{ticket.resuelto_por.nombre}</strong> marcó el ticket como{" "}
                  <strong>resuelto</strong> · {formatDateTime(ticket.resuelto_at)}
                </>
              ) : (
                <>
                  El cliente marcó el ticket como <strong>resuelto</strong> ·{" "}
                  {formatDateTime(ticket.resuelto_at)}
                </>
              )}
            </EventRow>
          )}
          {ticket.cerrado_at && (
            <EventRow icon={<Wrench size={12} />}>
              <strong>{ticket.cerrado_por?.nombre ?? "Sistema"}</strong> cerró el ticket ·{" "}
              {formatDateTime(ticket.cerrado_at)}
            </EventRow>
          )}

          {/* Form de respuesta */}
          {isCerrado ? (
            <div className="px-6 py-8 bg-zinc-50 dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-800 text-center text-sm text-zinc-500">
              Este ticket está cerrado. Si necesita seguimiento, abre uno nuevo.
            </div>
          ) : (
            <div className="px-6 py-5 bg-zinc-50 dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-800 max-md:px-3 max-md:py-4">
              <div className="mb-3 flex items-center justify-between max-md:flex-col max-md:items-start max-md:gap-1.5">
                <div className="inline-flex bg-zinc-100 dark:bg-zinc-800 rounded-lg p-0.5">
                  <button
                    type="button"
                    onClick={() => setIsInternal(false)}
                    className={cn(
                      "px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors",
                      !isInternal
                        ? "bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 shadow-sm"
                        : "text-zinc-500 dark:text-zinc-400",
                    )}
                  >
                    <MessageCircle size={12} />
                    Respuesta al cliente
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsInternal(true)}
                    className={cn(
                      "px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors",
                      isInternal
                        ? "bg-amber-600 text-white shadow-sm"
                        : "text-zinc-500 dark:text-zinc-400",
                    )}
                  >
                    <Lock size={12} />
                    Nota interna
                  </button>
                </div>
                <div className="text-[11px] text-zinc-500">
                  {isInternal
                    ? "Solo equipo · NO se manda al cliente"
                    : "El cliente recibirá email + push"}
                </div>
              </div>

              <div
                className={cn(
                  "rounded-lg p-3 transition-colors",
                  isInternal
                    ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900"
                    : "bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800",
                )}
              >
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={4}
                  placeholder={
                    isInternal
                      ? "Nota interna pa' el equipo (no se manda al cliente)…"
                      : `Responder a ${ticket.cliente.razon_social}…`
                  }
                  className="w-full text-sm bg-transparent border-0 outline-none resize-y placeholder:text-zinc-400"
                />
                {/* Preview del adjunto si hay (chip con thumbnail/icono) */}
                {attachment && (
                  <div className="mt-2 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1.5 dark:border-zinc-700 dark:bg-zinc-900">
                    {attachment.type.startsWith("image/") ? (
                      <img
                        src={URL.createObjectURL(attachment)}
                        alt={attachment.name}
                        className="size-10 rounded object-cover"
                      />
                    ) : (
                      <div className="flex size-10 items-center justify-center rounded bg-zinc-200 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                        <Paperclip size={14} />
                      </div>
                    )}
                    <div className="flex-1 overflow-hidden text-xs">
                      <div className="truncate text-zinc-700 dark:text-zinc-200">
                        {attachment.name}
                      </div>
                      <div className="text-[10px] text-zinc-500">
                        {(attachment.size / 1024).toFixed(0)} KB · {attachment.type}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAttachment(null)}
                      className="rounded p-1 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-200"
                      aria-label="Quitar adjunto"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between pt-3 mt-2 border-t border-zinc-200 dark:border-zinc-800 max-md:flex-wrap max-md:gap-2">
                  <label className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 cursor-pointer">
                    <Paperclip size={12} />
                    {attachment ? "Cambiar foto" : "Adjuntar foto"}
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0] ?? null;
                        if (f && f.size > 10 * 1024 * 1024) {
                          toast.error("Máximo 10 MB");
                          return;
                        }
                        setAttachment(f);
                        // limpiamos el input pa' poder re-seleccionar el mismo archivo
                        e.target.value = "";
                      }}
                    />
                  </label>
                  <div className="flex items-center gap-2 max-md:w-full max-md:[&>button]:flex-1 max-md:[&>button]:justify-center">
                    {!isInternal && (
                      <button
                        type="button"
                        onClick={() => submitReply({ resolveAfter: true })}
                        disabled={!reply.trim() || addCom.isPending || isUploading}
                        className="px-3 py-1.5 text-xs font-medium border border-zinc-200 dark:border-zinc-800 rounded-md bg-white dark:bg-zinc-950 hover:bg-zinc-50 dark:hover:bg-zinc-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Enviar y resolver
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => submitReply()}
                      disabled={!reply.trim() || addCom.isPending || isUploading}
                      className="px-3 py-1.5 text-xs font-medium bg-black hover:bg-zinc-800 text-white rounded-md flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addCom.isPending || isUploading ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Send size={12} />
                      )}
                      {isInternal ? "Guardar nota" : "Enviar respuesta"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar derecho */}
        <aside className="w-72 bg-zinc-50 dark:bg-zinc-900 border-l border-zinc-200 dark:border-zinc-800 overflow-y-auto p-4 space-y-3 flex-shrink-0 max-md:w-full max-md:border-l-0 max-md:border-t max-md:overflow-visible max-md:pb-6">
          <RightCard label="Cliente">
            <div className="flex items-start gap-2 mb-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                {avatarFromName(ticket.cliente.razon_social)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-zinc-900 dark:text-zinc-100 truncate">
                  {ticket.cliente.razon_social}
                </div>
                <div className="text-xs text-zinc-500 font-mono">{ticket.cliente.codigo}</div>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/clientes/${ticket.cliente.id}`)}
                className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300"
                title="Abrir ficha cliente"
              >
                <ExternalLink size={14} />
              </button>
            </div>
          </RightCard>

          <RightCard label="Acciones rápidas">
            <div className="space-y-0.5">
              {ticket.estado !== "resuelto" && ticket.estado !== "cerrado" && (
                <QuickAction
                  icon={<CheckCircle2 size={14} />}
                  onClick={() => setEstado("resuelto")}
                >
                  Marcar como resuelto
                </QuickAction>
              )}
              {ticket.estado === "resuelto" && (
                <QuickAction
                  icon={<Lock size={14} />}
                  onClick={() => setEstado("cerrado")}
                >
                  Cerrar ticket
                </QuickAction>
              )}
              {(ticket.estado === "resuelto" || ticket.estado === "cerrado") && (
                <QuickAction
                  icon={<ArrowRight size={14} />}
                  onClick={() => setEstado("en_proceso")}
                >
                  Reabrir ticket
                </QuickAction>
              )}
              {ticket.asignado?.id !== currentUser?.id && (
                <QuickAction icon={<UserPlus size={14} />} onClick={takeIt}>
                  Tomar ticket
                </QuickAction>
              )}
              <QuickAction
                icon={<UserPlus size={14} />}
                onClick={() => setAsignarOpen(true)}
              >
                Asignar a otro usuario
              </QuickAction>
            </div>
          </RightCard>

          <RightCard label="Auditoría">
            <div className="space-y-1.5 text-[11px] text-zinc-500">
              <div>
                Creado{" "}
                <strong className="text-zinc-700 dark:text-zinc-300">
                  {ticket.creado_por_cliente
                    ? "por el cliente"
                    : `por ${ticket.creador?.nombre ?? "—"}`}
                </strong>{" "}
                · {formatDateTime(ticket.creado_at)}
              </div>
              {ticket.asignado_at && ticket.asignado && (
                <div>
                  Asignado a{" "}
                  <strong className="text-zinc-700 dark:text-zinc-300">
                    {ticket.asignado.nombre}
                  </strong>{" "}
                  · {formatDateTime(ticket.asignado_at)}
                </div>
              )}
            </div>
          </RightCard>
        </aside>
      </div>

      {/* rc45 — Dialog asignar a otro user */}
      <AsignarTicketDialog
        open={asignarOpen}
        onOpenChange={setAsignarOpen}
        ticket={ticket}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sub-componentes auxiliares
// ─────────────────────────────────────────────────────────────────────────
function MetaCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className="text-zinc-500">{label}:</span>
      {children}
    </div>
  );
}

function Sep() {
  return <span className="text-zinc-300 dark:text-zinc-700">·</span>;
}

function PillSelect({
  value,
  options,
  className,
  onChange,
  disabled,
  prefix,
}: {
  value: string;
  options: { value: string; label: string }[];
  className?: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  prefix?: React.ReactNode;
}) {
  return (
    <label
      className={cn(
        "relative inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium cursor-pointer hover:ring-2 hover:ring-zinc-200 dark:hover:ring-zinc-800",
        disabled && "opacity-60 cursor-wait",
        className,
      )}
    >
      {prefix}
      <span>{options.find((o) => o.value === value)?.label ?? value}</span>
      <ChevronDown size={10} />
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-wait"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function CommentRow({
  authorName,
  authorTag,
  createdAt,
  body,
  internal,
  attachments,
}: {
  authorName: string;
  authorTag: "cliente" | "tecnico";
  createdAt: string;
  body: string;
  internal: boolean;
  attachments?: React.ReactNode;
}) {
  const avBg = authorTag === "cliente"
    ? "bg-gradient-to-br from-amber-400 to-amber-600"
    : "bg-violet-500";
  return (
    <div
      className={cn(
        "grid grid-cols-[40px_1fr] gap-3 px-6 py-4 border-b border-zinc-100 dark:border-zinc-900 max-md:px-3 max-md:gap-2",
        internal && "bg-amber-50 dark:bg-amber-950/30 border-l-4 border-l-amber-600",
      )}
    >
      <div
        className={cn(
          "w-9 h-9 rounded-full text-white flex items-center justify-center text-[11px] font-bold",
          avBg,
        )}
      >
        {avatarFromName(authorName)}
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <span className="font-medium text-zinc-900 dark:text-zinc-100 text-sm">{authorName}</span>
          <span
            className={cn(
              "text-[10px] px-1.5 py-0.5 rounded-full font-medium",
              authorTag === "cliente"
                ? "bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300"
                : "bg-sky-100 dark:bg-sky-950 text-sky-700 dark:text-sky-300",
            )}
          >
            {authorTag === "cliente" ? "Cliente" : "Técnico"}
          </span>
          {internal && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-amber-600 text-white inline-flex items-center gap-1">
              <Lock size={9} />
              Solo equipo
            </span>
          )}
          <span className="text-[11px] text-zinc-500">{formatDateTime(createdAt)}</span>
        </div>
        <div className="text-sm text-zinc-900 dark:text-zinc-100 whitespace-pre-wrap leading-relaxed">
          {body}
        </div>
        {attachments && <div className="mt-2">{attachments}</div>}
      </div>
    </div>
  );
}

function EventRow({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[40px_1fr] gap-3 px-6 py-2 items-center max-md:px-3 max-md:gap-2">
      <div className="w-6 h-6 rounded-full bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 flex items-center justify-center mx-auto">
        {icon}
      </div>
      <div className="text-[11px] text-zinc-500 leading-relaxed">{children}</div>
    </div>
  );
}

function RightCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">
        {label}
      </div>
      {children}
    </div>
  );
}

function QuickAction({
  icon,
  onClick,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-900 text-left"
    >
      <span className="text-zinc-500">{icon}</span>
      {children}
    </button>
  );
}
